// Connection types for three-tier architecture
// FR-023: HostingType determines who hosts the SFTP server
// - 'hosted': Partner connects to our SFTP server
// - 'remote': We connect to partner's SFTP server
export type HostingType = 'hosted' | 'remote';

// Legacy: ConnectionType for test panel backward compatibility (maps hosted=inbound, remote=outbound)
export type ConnectionType = 'inbound' | 'outbound';

// TransferMethod: protocol used for file transfer
// THUB-220: azure_blob is a first-class transport (peer of sftp + the universal api/HTTP type).
export type TransferMethod = 'sftp' | 'ftp' | 's3' | 'api' | 'azure_blob';

/**
 * Every transfer method, in one place (THUB-273).
 *
 * The Connections filter previously hardcoded a single `<option value="sftp">`, so
 * `azure_blob` (spec 020) and `api` (spec 019) were both missing and half the connections
 * in DEV could not be filtered at all. Deriving the options from this constant means adding
 * a method to the union above and forgetting the filter is no longer possible.
 *
 * `satisfies` ties it to the union: a value that is not a TransferMethod fails to compile,
 * and a method missing from the union is caught by the exhaustive label map below.
 */
export const TRANSFER_METHODS = [
  'sftp', 'ftp', 's3', 'api', 'azure_blob',
] as const satisfies readonly TransferMethod[];

/** Display labels. Typed as Record<TransferMethod, string> so a new method must be added. */
export const TRANSFER_METHOD_LABELS: Record<TransferMethod, string> = {
  sftp: 'SFTP',
  ftp: 'FTP',
  s3: 'S3',
  api: 'HTTP / API',
  azure_blob: 'Azure Blob',
};
export type ConnectionStatus = 'ready' | 'issues' | 'setting_up' | 'idle' | 'not_used' | 'unknown';

// T053: Provisioning status for resource lifecycle
export type ProvisioningStatus = 'pending' | 'provisioning' | 'active' | 'failed' | 'deleting';

// T053: Health indicator for operational status
export type HealthStatus = 'healthy' | 'warning' | 'error' | 'unknown';
export type AuthMethod = 'password' | 'key' | 'none';

// T023: HTTP/API auth types (019-http-transfer-capability)
export type HttpAuthType = 'none' | 'basic' | 'bearer' | 'oauth2_cc' | 'oauth2_custom';

// Connection test error codes for categorized failures.
// THUB-138: HOST_KEY_MISMATCH / PATH_NOT_FOUND / CONNECTION_REFUSED / DNS_FAILURE /
// ENDPOINT_ERROR were split out of the three codes that used to absorb them, so the GUI can
// show the right remedy -- in particular so the "ask the partner to open their firewall" hint
// appears ONLY for a genuine reachability failure. Keep in sync with ConnectionTestErrorCode
// in src/lambda/layers/shared/src/shared/models.py.
export type ConnectionTestErrorCode =
  | 'NETWORK_UNREACHABLE'
  | 'CONNECTION_REFUSED'
  | 'DNS_FAILURE'
  | 'AUTH_FAILED'
  | 'HOST_KEY_MISMATCH'
  | 'PATH_NOT_FOUND'
  | 'DIR_ACCESS_DENIED'
  | 'ENDPOINT_ERROR'
  | 'SECRET_ACCESS_ERROR'
  | 'TIMEOUT'
  | 'CONNECTOR_NOT_FOUND'
  | 'RATE_LIMITED'
  | 'TEST_IN_PROGRESS'
  | 'UNKNOWN_ERROR';

// Connection test stages
export type ConnectionTestStage = 'network' | 'authentication' | 'directory' | 'unknown';

export interface StageResult {
  success: boolean;
  duration?: number;      // milliseconds
  path?: string;          // for directory listing stage
  error?: string;
}

export interface TestStages {
  hostReachable?: StageResult;
  authentication?: StageResult;
  directoryListing?: StageResult;
}

/**
 * Runtime connection config shape.
 *
 * Uses camelCase keys because API responses are recursively camelized by
 * `snakeToCamel` at the service boundary (see `services/api/connections.ts`).
 * Components reading `connection.config.*` should use camelCase (e.g. `publicKeyContent`).
 *
 * For REQUEST/form shapes, use `ConnectionConfigWire` instead — those are sent
 * through `camelToSnake` and need to mirror the snake_case wire contract that
 * the forms were originally authored against.
 */
export interface ConnectionConfig {
  // Outbound connections (we connect to partner)
  host?: string;
  port?: number;
  username?: string;
  authMethod?: AuthMethod;
  password?: string;               // Password for auth (stored in Secrets Manager)
  passwordSecretArn?: string;      // AWS Secrets Manager ARN
  privateKeyContent?: string;      // Private key content (stored in Secrets Manager)
  privateKeyPath?: string;
  knownHostsPath?: string;
  trustedHostKeys?: string[];      // For Transfer Family TrustedHostKeys

  // Inbound connections (partner connects to us)
  publicKeyContent?: string;       // Partner's public key
  allowedIpRanges?: string[];

  // SSH key auto-generation (018-thub-5)
  generatedPublicKey?: string;     // OpenSSH public key from auto-gen or manual derive
  keyGenerationMethod?: string;    // "auto" or "manual"
  keyType?: 'rsa' | 'ecdsa';      // SSH key algorithm (THUB-113: default ecdsa)
  useExistingKey?: boolean;        // Request-only: true to paste existing private key

  // HTTP connection settings (019-http-transfer-capability)
  baseUrl?: string;
  httpAuthType?: HttpAuthType;
  httpMethod?: string;
  httpContentType?: string;
  httpTimeout?: number;
  oauth2TokenEndpoint?: string;
  oauth2ClientId?: string;
  oauth2GrantType?: string;
  oauth2Scope?: string;
  // Sensitive fields (sent to backend, stripped from responses)
  oauth2ClientSecret?: string;
  oauth2CustomParams?: Record<string, string>;
  bearerToken?: string;

  // Azure Blob connection settings (THUB-220). The SAS token is write-only: entered by the
  // value-stream team, sent on create/update, stored in Secrets Manager, never returned.
  // No path field: the connection owns auth + endpoint, the TASK owns its path
  // (destinationDirectory / sourceDirectory), same as SFTP and HTTP.
  azureContainerUrl?: string;      // https://<acct>.blob.core.windows.net/<container>
  sasToken?: string;               // write-only credential (stripped from responses)

  // Common settings
  timeout?: number;
  retryAttempts?: number;
  enableEncryption?: boolean;
  enableCompression?: boolean;
}

/**
 * Wire-format connection config used for Create/Update request payloads and
 * corresponding form state. Keys match what the backend expects on the wire
 * (snake_case for legacy SFTP/SSH/common fields, camelCase for newer HTTP/OAuth2
 * fields authored after the convention shift).
 *
 * The `camelToSnake` transform in `services/api/connections.ts` normalizes these
 * to snake_case before sending. Do NOT rename the snake_case keys here — the
 * existing form inputs (`CreateConnection.tsx`, `EditConnection.tsx`) bind to
 * them directly.
 */
export interface ConnectionConfigWire {
  // Outbound connections (we connect to partner)
  host?: string;
  port?: number;
  username?: string;
  auth_method?: AuthMethod;
  password?: string;
  password_secret_arn?: string;
  private_key_content?: string;
  private_key_path?: string;
  known_hosts_path?: string;
  trusted_host_keys?: string[];

  // Inbound connections (partner connects to us)
  public_key_content?: string;
  allowed_ip_ranges?: string[];

  // SSH key auto-generation (018-thub-5)
  generatedPublicKey?: string;
  keyGenerationMethod?: string;
  keyType?: 'rsa' | 'ecdsa';
  useExistingKey?: boolean;

  // HTTP connection settings (019-http-transfer-capability)
  baseUrl?: string;
  httpAuthType?: HttpAuthType;
  httpMethod?: string;
  httpContentType?: string;
  httpTimeout?: number;
  oauth2TokenEndpoint?: string;
  oauth2ClientId?: string;
  oauth2GrantType?: string;
  oauth2Scope?: string;
  oauth2ClientSecret?: string;
  oauth2CustomParams?: Record<string, string>;
  bearerToken?: string;

  // Azure Blob connection settings (THUB-220). camelCase -> camelToSnake maps to the
  // backend's azure_container_url / sas_token. No path field -- the task owns its path.
  azureContainerUrl?: string;
  sasToken?: string;               // write-only credential

  // Common settings
  timeout?: number;
  retry_attempts?: number;
  enable_encryption?: boolean;
  enable_compression?: boolean;
}

export interface ConnectionTestResult {
  success: boolean;
  responseTime?: number;
  error?: string;
  testedAt: string;                // ISO 8601 timestamp
  // Enhanced fields for error categorization
  errorCode?: ConnectionTestErrorCode;
  errorStage?: ConnectionTestStage;
  stages?: TestStages;
}

// Rate limit status for UI
export interface RateLimitStatus {
  testInProgress: boolean;
  rateLimited: boolean;
  retryAfter?: number;             // Seconds until test allowed
}

export interface Connection {
  // Primary Key
  connectionId: string;
  
  // Identity and Relationships
  name: string;
  partnerId: string;
  partnerName: string;               // Denormalized for display
  
  // Connection Type and Configuration
  // FR-023: hostingType determines who hosts the connection
  // - 'hosted': Partner connects to our SFTP server
  // - 'remote': We connect to partner's SFTP server
  hostingType: HostingType;
  transferMethod: TransferMethod;
  config: ConnectionConfig;
  
  // Status (single comprehensive status)
  status: ConnectionStatus;
  // T053: New provisioning and health status fields
  provisioningStatus?: ProvisioningStatus;  // Resource lifecycle status
  health?: HealthStatus;                     // Operational health indicator
  lastConnectionTest?: string;       // ISO 8601 timestamp
  lastSuccessfulConnection?: string; // ISO 8601 timestamp
  connectionTestResults?: ConnectionTestResult;
  
  // AWS Resources
  transferFamilyUserArn?: string;    // For inbound connections
  transferFamilyConnectorArn?: string; // For outbound connections
  connectorEgressIps?: string[];     // Static egress IPs for partner whitelisting
  
  // Computed Metrics
  taskCounts: {
    total: number;
    enabled: number;
    disabled: number;
  };

  // Rate Limiting (UI state)
  testInProgress?: boolean;
  testInProgressTtl?: number;        // Epoch timestamp for auto-unlock
  lastTestRateLimitAt?: string;      // ISO timestamp for rate limiting

  // PGP Encryption (021-pgp-encryption)
  pgpPublicKeyConfigured?: boolean;
  pgpPrivateKeyConfigured?: boolean;
  pgpKeyFingerprint?: string;
  pgpKeyExpiration?: string;         // ISO 8601 timestamp
  pgpKeyWarning?: string;            // Warning when key expires within 30 days

  // Metadata
  createdAt: string;                 // ISO 8601 timestamp
  updatedAt: string;                 // ISO 8601 timestamp
  createdBy: string;

  // Optimistic Locking
  version?: number;                  // Version number for optimistic locking
}

export interface CreateConnectionRequest {
  name: string;
  partnerId: string;
  hostingType: HostingType;          // FR-023: hosted/remote
  transferMethod: TransferMethod;
  config: ConnectionConfigWire;
}

export interface UpdateConnectionRequest extends Partial<CreateConnectionRequest> {
  status?: ConnectionStatus;
  version?: number;                  // Version for optimistic locking
  // PGP Encryption (021-pgp-encryption)
  pgpPublicKey?: string;             // ASCII-armored PGP public key
  pgpPrivateKey?: string;            // ASCII-armored PGP private key
  pgpPrivateKeyPassphrase?: string;  // Passphrase for private key
}

export interface ConnectionFilters {
  status: (ConnectionStatus | ProvisioningStatus)[];
  hostingType: HostingType[];
  transferMethod: TransferMethod[];
  partners: string[];
}

export type ConnectionSortField = 'name' | 'partnerName' | 'hostingType' | 'transferMethod' | 'status' | 'createdAt' | 'lastConnectionTest';

// Connection test history record
export interface ConnectionTestHistory {
  testId: string;
  connectionId: string;
  partnerId: string;
  testType: 'manual' | 'scheduled' | 'pre_creation';
  testResults: ConnectionTestResult;
  triggeredBy: 'user' | 'system' | 'schedule';
  userId?: string;
  createdAt: string;
}